<template>
  <v-alert
    :type="vuetifyType"
    :color="alertColor"
    variant="tonal"
    :closable="closable"
    :density="density"
    :class="['base-alert', alertClass]"
    @click:close="$emit('close')"
  >
    <template v-if="$slots.prepend || icon" #prepend>
      <slot name="prepend">
        <v-icon v-if="icon" :icon="alertIcon" :size="iconSize" />
      </slot>
    </template>
    <slot>{{ message }}</slot>
    <template v-if="$slots.append" #append>
      <slot name="append"></slot>
    </template>
  </v-alert>
</template>

<script setup>
import { computed } from 'vue';
import { useThemeColors } from '../composables/useThemeColors.js';

defineOptions({ name: 'BaseAlert' });

const { lightBlueDarken3 } = useThemeColors();

const props = defineProps({
  message: {
    type: String,
    default: ''
  },
  /**
   * Alert type:
   * - 'info': Blue informational (Vuetify default)
   * - 'success': Green success
   * - 'warning': Orange warning
   * - 'error': Red error
   * - 'inactive': Light blue (for inactive/disabled states)
   * - 'neutral': Grey neutral info
   */
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'success', 'warning', 'error', 'inactive', 'neutral'].includes(value)
  },
  closable: {
    type: Boolean,
    default: false
  },
  icon: {
    type: [String, Boolean],
    default: undefined
  },
  iconSize: {
    type: [String, Number],
    default: 'default'
  },
  density: {
    type: String,
    default: 'compact',
    validator: (value) => ['default', 'comfortable', 'compact'].includes(value)
  },
  alertClass: {
    type: String,
    default: ''
  }
});

// Map custom types to Vuetify types (for icon defaults)
const vuetifyType = computed(() => {
  const typeMap = {
    inactive: 'info',
    neutral: 'info'
  };
  return typeMap[props.type] || props.type;
});

// Centralized color mapping
const alertColor = computed(() => {
  const colorMap = {
    info: undefined, // Use Vuetify default
    success: undefined,
    warning: undefined,
    error: undefined,
    inactive: lightBlueDarken3.value,
    neutral: '#78909C' // blueGreyLighten1
  };
  return colorMap[props.type];
});

const alertIcon = computed(() => {
  if (props.icon) return props.icon;
  const icons = {
    info: '$info',
    success: '$success',
    warning: '$warning',
    error: '$error',
    inactive: '$info',
    neutral: '$info'
  };
  return icons[props.type] || '$info';
});

defineEmits(['close']);
</script>

<style scoped>
.base-alert {
  border-radius: 8px !important;
  font-size: 0.9rem;
}

.base-alert :deep(.v-alert__prepend) {
  margin-right: 12px;
  align-self: flex-start;
}

.base-alert :deep(.v-alert__content) {
  line-height: 1.4;
}
</style>
